import {
  Component,
  computed,
  DestroyRef,
  ElementRef,
  inject,
  input,
  output,
  signal,
  viewChild,
} from '@angular/core';
import { DOCUMENT } from '@angular/common';
import { TranslatePipe } from '@ngx-translate/core';
import { HlmPopoverImports } from '@spartan-ng/helm/popover';
import { STATUS_OPTIONS, StatusOption, StatusValue } from './status-dropdown.interfaces';
import {
  DropdownCoordinationService,
  getNextOptionIndex,
  getPreviousOptionIndex,
} from '../dropdown-coordination.service';
import { ThemedIconComponent } from '../../atoms/themed-icon/themed-icon.component';

@Component({
  selector: 'ocp-status-dropdown',
  imports: [TranslatePipe, HlmPopoverImports, ThemedIconComponent],
  templateUrl: './status-dropdown.component.html',
  styleUrl: './status-dropdown.component.scss',
  host: {
    class: 'status-dropdown',
  },
})
export class StatusDropdownComponent {
  private readonly document = inject(DOCUMENT);
  private readonly dropdownCoordination = inject(DropdownCoordinationService);
  private readonly elementRef = inject(ElementRef);
  private readonly destroyRef = inject(DestroyRef);

  /** Unique instance ID for generating globally unique element IDs */
  private readonly instanceId = this.dropdownCoordination.generateInstanceId();

  readonly style = input<string>('');

  /** Current selected status value */
  readonly value = input<StatusValue>(null);

  /** Optional placeholder shown when no value is selected (overrides default 'Not set' text) */
  readonly placeholder = input<string>('');

  /** Accessible label for the dropdown trigger */
  readonly ariaLabel = input<string>('');

  /** Size (px) of the chevron icon — override per consumer without affecting other usages */
  readonly chevronSize = input<number>(7);

  /** Chevron icon filename — override per consumer without affecting other usages */
  readonly chevronIcon = input<string>('chevron-down.svg');

  /** Emits when a status is selected */
  readonly statusChange = output<StatusValue>();

  /** Emits when the dropdown popover is opened */
  readonly editorOpen = output<void>();

  /** Emits when the dropdown popover is closed */
  readonly editorClose = output<void>();

  /** Track popover open state for aria-expanded */
  protected readonly isOpen = signal(false);

  /** Drives programmatic close; resets to null after the overlay acknowledges the close */
  protected readonly overlayState = signal<'open' | 'closed' | null>(null);

  /** Width of the trigger element — used to size the popover to match */
  protected readonly triggerWidth = signal<string>('auto');
  private readonly trigger = viewChild<ElementRef<HTMLButtonElement>>('trigger');

  constructor() {
    this.dropdownCoordination.registerCloseHandler(
      this.elementRef,
      () => this.closePopover(),
      this.destroyRef
    );
  }

  private closePopover(): void {
    this.overlayState.set('closed');
  }

  /** All available status options */
  readonly options: StatusOption[] = STATUS_OPTIONS;

  /** Track which option is focused for keyboard navigation */
  protected readonly focusedIndex = signal<number>(-1);

  /** Generate unique ID for an option */
  protected getOptionId(index: number): string {
    return `status-option-${this.instanceId}-${index}`;
  }

  /** Get the ID of the currently focused option for aria-activedescendant */
  protected readonly activeDescendant = computed(() => {
    const index = this.focusedIndex();
    return index >= 0 ? this.getOptionId(index) : undefined;
  });

  /** Currently selected option based on value */
  protected readonly selectedOption = computed<StatusOption | undefined>(() => {
    const currentValue = this.value();
    return this.options.find(opt => opt.value === currentValue);
  });

  /** CSS class modifier for the selected status */
  protected readonly selectedModifier = computed<string>(() => {
    return this.selectedOption()?.modifier ?? '';
  });

  protected onTriggerClick(): void {
    this.dropdownCoordination.notifyOpen(this.elementRef);
  }

  /** Handle option selection */
  selectOption(option: StatusOption, event: Event): void {
    event.stopPropagation();
    this.statusChange.emit(option.value);
    this.closePopover();
    this.focusedIndex.set(-1);
  }

  /** Handle keyboard navigation in the dropdown */
  onOptionKeydown(event: KeyboardEvent, option: StatusOption, index: number): void {
    switch (event.key) {
      case 'Enter':
      case ' ':
        event.preventDefault();
        this.selectOption(option, event);
        break;
      case 'ArrowDown':
        event.preventDefault();
        this.focusNextOption(index);
        break;
      case 'ArrowUp':
        event.preventDefault();
        this.focusPreviousOption(index);
        break;
      case 'Escape':
        event.preventDefault();
        this.closePopover();
        this.focusedIndex.set(-1);
        break;
    }
  }

  protected onStateChanged(state: 'open' | 'closed'): void {
    if (state === 'closed') {
      this.overlayState.set(null);
    }
    this.isOpen.set(state === 'open');
    if (state === 'open') {
      const triggerWidth = this.trigger()?.nativeElement.offsetWidth;
      this.triggerWidth.set(triggerWidth ? `${triggerWidth}px` : 'auto');
      this.editorOpen.emit();
      const selectedIndex = this.options.findIndex(opt => opt.value === this.value());
      this.focusedIndex.set(Math.max(selectedIndex, 0));
    } else {
      this.focusedIndex.set(-1);
      this.editorClose.emit();
    }
  }

  /** Check if an option is the currently selected one */
  isSelected(option: StatusOption): boolean {
    return option.value === this.value();
  }

  /** Track by function for ngFor optimization */
  trackByValue(_index: number, option: StatusOption): StatusValue {
    return option.value;
  }

  private focusNextOption(currentIndex: number): void {
    const nextIndex = getNextOptionIndex(currentIndex, this.options.length);
    this.focusedIndex.set(nextIndex);
    this.focusOptionElement(nextIndex);
  }

  private focusPreviousOption(currentIndex: number): void {
    const prevIndex = getPreviousOptionIndex(currentIndex, this.options.length);
    this.focusedIndex.set(prevIndex);
    this.focusOptionElement(prevIndex);
  }

  private focusOptionElement(index: number): void {
    setTimeout(() => {
      const element = this.document.getElementById(this.getOptionId(index));
      element?.focus();
    });
  }
}
